<template>
	<header
		:class="[
			'container max-container-width position-relative',
			thirdPartyAuthStore.isSignupEnabled ? 'pt-100' : 'pt-500-lg pt-400'
		]"
	>
		<div class="display-flex align-items-center align-items-stretch-lg flex-wrap-wrap overflow-hidden">
			<div
				:class="[
					'col-12 col-6-md pt-300 pb-150 pt-400-lg',
					{ 'pr-300-md': !thirdPartyAuthStore.isSignupEnabled }
				]"
			>
				<div v-if="showBrandLogos" class="display-flex align-items-center justify-content-center-smo mb-300">
					<BaseLink :link="logoLink">
						<img :src="logoUrl" alt="Fundrise logo" class="logo-width" />
					</BaseLink>
				</div>

				<h1 class="heading-xl text-align-center-smo" data-test="hero-heading">
					Invest in a better alternative
				</h1>
				<p class="body-lg mt-150 col-11-lg text-align-center-smo" data-test="hero-description">
					{{ subheadingText }}
				</p>

				<div
					v-reveal:fade-in
					:class="[
						'display-flex mt-100 flex-direction-column',
						{ 'flex-direction-column-reverse': showLeadCapture && thirdPartyAuthStore.isSignupEnabled }
					]"
				>
					<LeadCaptureComponent
						v-if="showLeadCapture"
						class="mt-100"
						:form-config="mergedLeadCaptureConfig"
					/>
					<BaseLink v-else class="button button-primary mt-100 width-100-smo" :link="signupLink" />

					<p data-test="hero-microcopy" class="body-sm body-xs-smo text-align-center-smo mt-100">
						{{ microCopy }}
					</p>
				</div>

				<SocialProofSection v-if="!appStore.isMobile" />
			</div>
			<div
				:class="[
					'hero-image-container col-12 col-6-md display-flex-smo justify-content-center-smo',
					{
						'pl-500-lg': !thirdPartyAuthStore.isSignupEnabled,
						'pl-400-lg': thirdPartyAuthStore.isSignupEnabled
					}
				]"
			>
				<figure class="hero-image m-0">
					<img
						:src="`${appCloudfrontPath}/images/marketing-site/hero-images/homepage-hero.webp`"
						alt="mobile phones with Fundrise app on display"
						fetchpriority="high"
						crossorigin="anonymous"
					/>
				</figure>
			</div>
		</div>
		<SocialProofSection v-if="appStore.isMobile" class="mb-150" />
	</header>
</template>

<script setup lang="ts">
import { computed, reactive } from 'vue';
import { appCloudfrontPath } from '@constants';
import LeadCaptureComponent from '@components/lead-capture/lead-capture-form.vue';
import { LeadCaptureFormConfig } from 'types/layout/lead';
import { Link } from 'types/layout';
import { PromoInvestmentType } from 'types/promos';
import SocialProofSection from './social-proof-section.vue';
import { useAppStore } from '@stores/app';
import { useThirdPartyAuthStore } from '@stores/third-party-auth';

interface Props {
	leadCaptureFeeWaiver?: string;
	leadCaptureReferralKey?: string;
	promoInvestmentType?: PromoInvestmentType;
	showBrandLogos?: boolean;
	showLeadCapture?: boolean;
	microCopy?: string;
	loading?: boolean;
	leadCaptureConfigOverride?: Partial<LeadCaptureFormConfig>;
}

const props = withDefaults(defineProps<Props>(), {
	leadCaptureFeeWaiver: undefined,
	leadCaptureReferralKey: undefined,
	promoInvestmentType: undefined,
	showBrandLogos: false,
	showLeadCapture: false,
	microCopy: 'Start investing in less than 5 minutes and with as little as $10.',
	loading: false,
	leadCaptureConfigOverride: undefined
});

const appStore = useAppStore();
const thirdPartyAuthStore = useThirdPartyAuthStore();

const leadCaptureConfig: LeadCaptureFormConfig = reactive({
	buttonText: 'Sign up',
	textCssClass: 'delta',
	signupFlowType: 'STANDARD'
});

const logoUrl = `${appCloudfrontPath}/interface/logo-hz-color.svg`;

const mergedLeadCaptureConfig = computed(() => ({
	...leadCaptureConfig,
	...(props.leadCaptureConfigOverride ?? {})
}));

const signupLink: Link = { text: 'Sign up', router: 'checkout-start' };

const logoLink = computed((): Link => {
	return appStore.isAuthenticated ? { router: 'account-overview' } : { router: 'home' };
});

const subheadingText = computed(() => {
	return 'Build a portfolio of private assets like real estate, private credit, and venture capital.';
});

async function setup(): Promise<void> {
	if (props.leadCaptureFeeWaiver) {
		leadCaptureConfig.feeWaiver = props.leadCaptureFeeWaiver;
	}

	if (props.promoInvestmentType) {
		leadCaptureConfig.promoInvestmentType = props.promoInvestmentType;
	}

	if (props.leadCaptureReferralKey) {
		leadCaptureConfig.referralKey = props.leadCaptureReferralKey;
	}
}

setup();
</script>

<style lang="scss" scoped>
@use '../../styles/utilities/respond-to.scss' as *;

.hero-image-container {
	.hero-image {
		height: 100%;

		img {
			max-height: 100%;
			object-fit: contain;
		}
	}

	@include respond-to(lg) {
		.hero-image {
			margin-top: 50px;
		}
	}
}

.logo-width {
	width: 137px;
}

:deep(.homepage-button-copy-test .lead-capture__fieldset > .mb-200) {
	margin-bottom: 0.5rem;
}
</style>
